import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import ChurchFlowLogo from "@/components/ChurchFlowLogo";
import { toast } from "sonner";
import { CheckCircle2, AlertTriangle } from "lucide-react";

export default function ResetPasswordPage() {
  const { token } = useParams();
  const nav = useNavigate();
  const { refreshOrgs } = useAuth();
  const [verifying, setVerifying] = useState(true);
  const [linkValid, setLinkValid] = useState(false);
  const [accountEmail, setAccountEmail] = useState("");
  const [error, setError] = useState("");
  const [form, setForm] = useState({ password: "", confirm: "" });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/auth/password-reset/verify/${token}`);
        setAccountEmail(data.email);
        setLinkValid(true);
      } catch (e) { setError(formatApiError(e)); }
      finally { setVerifying(false); }
    })();
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (form.password.length < 6) return toast.error("Password must be at least 6 characters");
    if (form.password !== form.confirm) return toast.error("Passwords don't match");
    setSubmitting(true);
    try {
      const { data } = await api.post("/auth/password-reset/confirm", { token, password: form.password });
      localStorage.setItem("ledger_token", data.token);
      if (refreshOrgs) await refreshOrgs();
      setDone(true);
      toast.success("Password updated");
      setTimeout(() => nav("/dashboard", { replace: true }), 900);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSubmitting(false); }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <Card className="w-full max-w-md rounded-3xl border-border shadow-none p-8">
        <div className="flex items-center gap-3 mb-6">
          <ChurchFlowLogo size={40} className="soft-glow rounded-2xl" />
          <div className="font-serif text-2xl">ChurchFlow</div>
        </div>

        {verifying ? (
          <div className="text-muted-foreground">Verifying link…</div>
        ) : !linkValid ? (
          <div className="space-y-4">
            <div className="w-12 h-12 rounded-2xl bg-destructive/10 text-destructive flex items-center justify-center">
              <AlertTriangle className="w-6 h-6" />
            </div>
            <h1 className="font-serif text-3xl tracking-tight">Link unavailable</h1>
            <p className="text-muted-foreground">{error}</p>
            <Link to="/forgot-password"><Button className="rounded-xl w-full" data-testid="reset-request-new">Request a new link</Button></Link>
            <Link to="/login" className="block text-center text-sm text-muted-foreground hover:text-foreground">Back to sign in</Link>
          </div>
        ) : done ? (
          <div className="space-y-4" data-testid="reset-done">
            <div className="w-12 h-12 rounded-2xl bg-primary/10 text-primary flex items-center justify-center">
              <CheckCircle2 className="w-6 h-6" />
            </div>
            <h1 className="font-serif text-3xl tracking-tight">Password updated</h1>
            <p className="text-muted-foreground">Signing you in…</p>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-5" data-testid="reset-form">
            <div>
              <h1 className="font-serif text-3xl tracking-tight">Choose a new password</h1>
              <p className="text-muted-foreground mt-2 leading-relaxed text-sm">
                For account <span className="font-medium text-foreground">{accountEmail}</span>.
              </p>
            </div>
            <div className="space-y-1.5">
              <Label>New password</Label>
              <Input type="password" autoFocus required minLength={6} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="rounded-xl h-12" placeholder="At least 6 characters" data-testid="reset-password-input" />
            </div>
            <div className="space-y-1.5">
              <Label>Confirm new password</Label>
              <Input type="password" required value={form.confirm} onChange={(e) => setForm({ ...form, confirm: e.target.value })} className="rounded-xl h-12" data-testid="reset-confirm-input" />
            </div>
            <Button type="submit" disabled={submitting} className="w-full h-12 rounded-xl soft-glow" data-testid="reset-submit-button">
              {submitting ? "Updating…" : "Update password & sign in"}
            </Button>
          </form>
        )}
      </Card>
    </div>
  );
}
